import { Form, Input, TreeSelect } from 'formik-antd';
import { Formik } from 'formik';
import { Button, Checkbox, Modal } from 'antd';
import * as Yup from 'yup';
import cx from 'classnames';
import React, { useRef, useState } from 'react';
import { isNil } from 'lodash';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import Radio from 'components/formik/Radio';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import TerminateAssignmentUploader from '../TerminateAssignmentUploader';
import SubmitButton from 'components/formik/SubmitButton';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  AssignmentTerminationCreateUpdateApiModel,
  AssignmentTerminationModel,
  AssignmentTerminationReasonTypeEnum,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import {
  deleteAssignmentTermination,
  saveTerminateAssignment,
  updateAssignmentTermination,
} from 'redux/reducers/assignmentSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getTerminationAssignmentLabel } from 'utils/assignmentUtils';
import { TerminationModeEnum } from '../TerminateAssignmentModal';
import { BaseFileYupObject } from 'components/formik/FormikFileUpload';
import { FileValue } from 'components/wrappers/BaseFileUpload';

import styles from '../../FinancialInfoScheduleModal/FinancialInfoScheduleModal.module.less';

const validationSchema = Yup.object({
  terminationDate: Yup.date().nullable().required('Field is required'),
  terminationReason: Yup.number().nullable().required('Field is required'),
  eligibleForRehire: Yup.boolean().required('Field is required'),
  details: Yup.string()
    .nullable()
    .max(250, 'Termination cause details should be 250 characters maximum'),
  terminateFileKeys: Yup.array().of(BaseFileYupObject),
});

interface FormValues {
  terminationDate: Date | undefined;
  terminationReason?: AssignmentTerminationReasonTypeEnum;
  eligibleForRehire?: boolean;
  details?: string;
  terminateFileKeys: FileValue[];
}

const initialValues: FormValues = {
  terminationDate: undefined,
  terminationReason: undefined,
  eligibleForRehire: undefined,
  details: undefined,
  terminateFileKeys: [],
};

type Props = {
  onClose: () => void;
  assignmentId: string;
  onSuccess?: () => Promise<void>;
  assignmentTerminationDetails?: AssignmentTerminationModel;
  assignmentTerminationFiles?: ResourceDownloadResponse[];
  isPendingAssignment?: boolean;
  mode?: TerminationModeEnum;
  title?: string;
};

export default function TerminationAssignmentForm(props: Props) {
  const {
    onClose,
    assignmentId,
    onSuccess,
    assignmentTerminationDetails,
    assignmentTerminationFiles,
    isPendingAssignment,
    mode,
    title = 'Terminate Assignment',
  } = props;

  const isUpdateMode = !isNil(assignmentTerminationDetails);
  const [isDisabledFields, setIsDisabledFields] = useState(isUpdateMode);
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const formRef: any = useRef();
  const isViewMode = mode === TerminationModeEnum.View;

  const handleIsDisabledFields = () => {
    setIsDisabledFields(!isDisabledFields);
  };

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const handleSubmit = async (data: FormValues) => {
    const values: AssignmentTerminationCreateUpdateApiModel = {
      assignmentId,
      terminationDate: data.terminationDate,
      terminationReason: data.terminationReason,
      eligibleForRehire: data.eligibleForRehire,
      details: data.details,
      attachments: data.terminateFileKeys.map((file) => ({
        fileKey: file.s3Key,
        fileName: file.fileName,
      })),
    };

    const correctAction = isUpdateMode
      ? updateAssignmentTermination
      : saveTerminateAssignment;

    const result = await dispatch(correctAction({ data: values, isAdmin }));

    if (correctAction.fulfilled.match(result)) {
      onSuccess && onSuccess();
      BaseAlert.success(
        isUpdateMode
          ? 'Changes have been scheduled.'
          : 'Assignment terminated successfully.',
      );
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onRemoveTermination = async () => {
    const result = await dispatch(
      deleteAssignmentTermination({ assignmentId, isAdmin }),
    );

    if (deleteAssignmentTermination.fulfilled.match(result)) {
      onSuccess && (await onSuccess());
      BaseAlert.success('Scheduled assignment termination has been removed.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleRemoveSchedule = () => {
    return Modal.confirm({
      title: 'Scheduled assignment termination will be removed. Are you sure?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        onRemoveTermination && (await onRemoveTermination());
      },
      onCancel() {},
    });
  };
  const getInitialValues = (): FormValues => {
    if (!isNil(assignmentTerminationDetails)) {
      return {
        terminationDate: assignmentTerminationDetails.terminationDate,
        terminationReason: assignmentTerminationDetails.terminationReason,
        eligibleForRehire: assignmentTerminationDetails.eligibleForRehire,
        details: assignmentTerminationDetails.details,
        terminateFileKeys: assignmentTerminationDetails.attachments?.length
          ? assignmentTerminationDetails.attachments.map((attachment) => ({
              fileName: attachment.fileName || '',
              s3Key: attachment.fileKey || '',
            }))
          : [],
      };
    }
    return initialValues;
  };

  const terminationReasonItems = () => {
    const items = [
      {
        title: (
          <>
            <i className={styles.dot} />
            {getTerminationAssignmentLabel(
              AssignmentTerminationReasonTypeEnum.AssignmentComplete,
            )}
          </>
        ),
        value: AssignmentTerminationReasonTypeEnum.AssignmentComplete,
        key: AssignmentTerminationReasonTypeEnum.AssignmentComplete,
        className: styles.line,
      },
      {
        title: 'Voluntary',
        value: 'Voluntary',
        disabled: true,
        className: styles.line,
        children: [
          {
            title: (
              <>
                <i className={styles.dot} />
                {getTerminationAssignmentLabel(
                  AssignmentTerminationReasonTypeEnum.Converted,
                )}
              </>
            ),
            value: AssignmentTerminationReasonTypeEnum.Converted,
            key: AssignmentTerminationReasonTypeEnum.Converted,
          },
          {
            title: (
              <>
                <i className={styles.dot} />
                {getTerminationAssignmentLabel(
                  AssignmentTerminationReasonTypeEnum.Resigned,
                )}
              </>
            ),
            value: AssignmentTerminationReasonTypeEnum.Resigned,
            key: AssignmentTerminationReasonTypeEnum.Resigned,
          },
        ],
      },
      {
        title: 'Involuntary',
        value: 'Involuntary',
        disabled: true,
        className: styles.line,
        children: [
          {
            title: (
              <>
                <i className={styles.dot} />
                {getTerminationAssignmentLabel(
                  AssignmentTerminationReasonTypeEnum.AttendanceIssue,
                )}
              </>
            ),
            value: AssignmentTerminationReasonTypeEnum.AttendanceIssue,
            key: AssignmentTerminationReasonTypeEnum.AttendanceIssue,
          },
          {
            title: (
              <>
                <i className={styles.dot} />
                {getTerminationAssignmentLabel(
                  AssignmentTerminationReasonTypeEnum.PerformanceIssue,
                )}
              </>
            ),
            value: AssignmentTerminationReasonTypeEnum.PerformanceIssue,
            key: AssignmentTerminationReasonTypeEnum.PerformanceIssue,
          },
          {
            title: (
              <>
                <i className={styles.dot} />
                {getTerminationAssignmentLabel(
                  AssignmentTerminationReasonTypeEnum.MisconductIssue,
                )}
              </>
            ),
            value: AssignmentTerminationReasonTypeEnum.MisconductIssue,
            key: AssignmentTerminationReasonTypeEnum.MisconductIssue,
          },
        ],
      },
    ];

    if (isViewMode || isPendingAssignment) {
      items.push({
        title: (
          <>
            <i className={styles.dot} />
            {getTerminationAssignmentLabel(
              AssignmentTerminationReasonTypeEnum.AssignmentCancelled,
            )}
          </>
        ),
        value: AssignmentTerminationReasonTypeEnum.AssignmentCancelled,
        key: AssignmentTerminationReasonTypeEnum.AssignmentCancelled,
        className: styles.line,
      });
    }

    return items;
  };

  const downloadTerminationFile = async (
    downloadResponse: ResourceDownloadResponse,
  ) => {
    try {
      const { downloadUrl, fileName } = downloadResponse;

      await downloadFile({ downloadUrl, fileName });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  return (
    <BaseModal title={title} onCancel={handleModalCancel} maxWidth={444} open>
      <Formik
        innerRef={formRef}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            {isUpdateMode && !isViewMode && (
              <div className={styles.checkboxWrap}>
                <Checkbox onChange={handleIsDisabledFields}>
                  Update scheduled termination
                </Checkbox>
              </div>
            )}
            <FormItem label="Last working day *" name="terminationDate">
              <DatePicker
                name="terminationDate"
                placeholder="Select last working day"
                showToday={false}
                format="MM/DD/YYYY"
                className="d-flex"
                disabled={isDisabledFields}
              />
            </FormItem>

            <FormItem name="terminationReason" label="Termination reason *">
              <TreeSelect
                name="terminationReason"
                placeholder="Select termination reason"
                disabled={isDisabledFields}
                treeDefaultExpandAll
                treeData={terminationReasonItems()}
                multiple={false}
              />
            </FormItem>

            <FormItem
              className={cx('inline-radio')}
              label="Eligible for rehire? *"
              name="eligibleForRehire"
            >
              <Radio.Group
                className="radio"
                name="eligibleForRehire"
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                disabled={isDisabledFields}
              />
            </FormItem>

            <FormItem
              name="details"
              label="Please provide more details on the termination cause"
            >
              <Input.TextArea
                name="details"
                placeholder="Enter termination cause details"
                autoComplete="off"
                disabled={isDisabledFields}
              />
            </FormItem>

            {isViewMode && Boolean(assignmentTerminationFiles?.length) && (
              <>
                <h5 className="modal-title">Supporting documents</h5>
                {assignmentTerminationFiles?.map((file) => (
                  <div
                    className={styles.link}
                    onClick={() => downloadTerminationFile(file)}
                  >
                    <i className="icon icon-paperclip" />
                    {getFileNameWithoutExt(file.fileName)}
                  </div>
                ))}
              </>
            )}

            {!isViewMode && (
              <>
                <h5 className="modal-title">
                  Please Upload Documentation - max 3 (resignation letter,
                  email, SMS)
                </h5>
                <div className="upload-wrapper">
                  <FormItem name="terminateFileKeys" showValidateError={false}>
                    <TerminateAssignmentUploader
                      fieldName="terminateFileKeys"
                      files={assignmentTerminationFiles}
                      isAdmin={isAdmin}
                      disabled={isDisabledFields}
                    />
                  </FormItem>
                </div>
              </>
            )}
          </div>
          <BaseModalFooter>
            {isViewMode ? (
              <Button type="primary" onClick={onClose}>
                Done
              </Button>
            ) : (
              <>
                <Button onClick={handleModalCancel}>Cancel</Button>
                {isUpdateMode && (
                  <Button type="primary" onClick={handleRemoveSchedule}>
                    Remove Termination
                  </Button>
                )}
                <SubmitButton type="primary">
                  {isUpdateMode ? 'Update' : 'Terminate'}
                </SubmitButton>
              </>
            )}
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
